import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { useRole } from '../hooks/useRole';
import { canDeleteForever } from '../utils/deleteGuard';
import { useExport } from './header/exporters';
import { useNavigate } from 'react-router-dom';
import { entityPath } from '../utils/entityRoutes';
import { VenueData, FloorPlan, Room } from '../types/Planner';
import { GuestGroup } from '../types/ui/Guests';
import {
    loadVenuesFromApi,
    getCachedVenues,
    apiSaveVenueDetails,
    apiSaveFloorPlan,
    apiSetVenueArchived,
    apiDeleteVenue,
} from '../services/VenueApiService';
import {
    loadFloorPlanTemplates,
    apiCreateFloorPlanTemplate,
    apiDeleteFloorPlanTemplate,
    FloorPlanTemplate,
} from '../services/FloorPlanTemplateApiService';
import { FloorPlanEditorFabric } from './FloorPlanEditorFabric';
import { GuestSeatingPanel } from './GuestSeatingPanel';
import { SharedFloorPlanView } from './SharedFloorPlanView';
import ConfirmDialog from './ui/ConfirmDialog';
import SearchableSelect from './ui/SearchableSelect';
import StatusBadge from './ui/StatusBadge';

import EmptyState from './EmptyState';
import Icon from './Icon';
import ContentCard from './ContentCard';
import { fetchSeatingTables, matchSection, SeatingTable } from '../services/SeatingService';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { recordLabel } from '../utils/recordId';
import { useRecordRefs } from '../utils/recordRefs';
import { useNotifications } from './NotificationProvider';
import { loadGuestGroups, loadGuests } from '../services/GuestService';
import { Guest } from '../types/ui/Guests';
import { uploadImage, resolveMediaUrl } from '../services/uploadService';
import { presetFor } from './floor-plan-editor/elementPresets';
import { resolveToken, labelForRoom } from './floor-plan-editor/floorPlanGeometry';
import { crescentFacing } from './floor-plan-editor/layoutContext';
import { crescentLabelPoint, crescentOutline } from './floor-plan-editor/tableGeometry';
import '../styles/components/VenueManager.css';
import { PHONE_PLACEHOLDER } from '../constants/placeholders';
import RecordListPublisher from './RecordListPublisher';

// Kept for external callers that still reference the type
export interface VenueBreadcrumbState {
    venueName: string | null;
    floorPlanName: string | null;
}

interface VenueManagerProps {
    venueId?: string;
    onBreadcrumbState?: (state: VenueBreadcrumbState) => void;
    statusFilter?: 'active' | 'archived' | 'all';
}

interface VenueForm {
    name: string;
    address: string;
    capacity: string;
    phone: string;
    email: string;
    website: string;
    notes: string;
    amenities: string;
    capacitySeatedBanquet: string;
    capacitySeatedConference: string;
    capacityStanding: string;
}

const ZONE_COLORS: Record<string, string> = {
    ceremony: '#3b82f6', reception: '#10b981', cocktail: '#f59e0b',
    dining: '#8b5cf6', dance: '#ef4444', stage: '#f97316',
    entrance: '#06b6d4', restroom: '#64748b', kitchen: '#84cc16', other: '#6b7280',
};

// Capacity estimation from drawn zone area, inspired by OnePlan's density-driven
// capacity calculator. Density figures are standard event-planning rules of thumb
// (sqft per guest), not precise — always shown as an editable suggestion, never
// auto-applied, since actual capacity depends on furniture/layout choices the
// canvas doesn't model.
const ZONE_DENSITY_SQFT_PER_GUEST: Partial<Record<Room['type'], number>> = {
    ceremony: 7,     // theater-style rows of chairs
    dining: 12,      // banquet round tables + aisle space
    reception: 8,    // mixed seated/standing reception area
    cocktail: 6,      // standing cocktail reception
    dance: 5,         // active dance floor
    stage: 10,        // head table / stage area, seated
};
const SQFT_PER_SQM = 10.7639;

function estimateZoneCapacity(room: Room, floorPlan: FloorPlan): number | null {
    const density = ZONE_DENSITY_SQFT_PER_GUEST[room.type];
    if (!density) return null;
    const areaPx2 = room.width * room.height;
    let areaSqFt: number | null = null;
    if (floorPlan.pixelsPerMeter && floorPlan.pixelsPerMeter > 0) {
        areaSqFt = (areaPx2 / (floorPlan.pixelsPerMeter ** 2)) * SQFT_PER_SQM;
    } else if (floorPlan.scale && floorPlan.scale > 0) {
        areaSqFt = areaPx2 / (floorPlan.scale ** 2);
    }
    if (areaSqFt == null) return null;
    return Math.max(1, Math.round(areaSqFt / density));
}

// GAP 5: Export floor plan as PNG (async, draws background image)
async function exportFloorPlanAsPNG(floorPlan: FloorPlan, venueName: string): Promise<void> {
    const DPR = window.devicePixelRatio || 1;
    const W = 1000;
    const H = 700;
    const TITLE_H = 48;
    const PAD = 20;

    const canvas = document.createElement('canvas');
    canvas.width = W * DPR;
    canvas.height = H * DPR;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(DPR, DPR);

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, W - 1, H - 1);

    // Title
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 16px system-ui, sans-serif';
    ctx.fillText(`${venueName} — ${floorPlan.name}`, PAD, 30);

    // Canvas area bounds
    const areaX = PAD;
    const areaY = TITLE_H;
    const areaW = W - PAD * 2;
    const areaH = H - TITLE_H - PAD;

    ctx.fillStyle = '#f9fafb';
    ctx.fillRect(areaX, areaY, areaW, areaH);

    // GAP 5: Draw background photo if available
    if (floorPlan.imageUrl || floorPlan.imageData) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
            img.onload = () => {
                ctx.globalAlpha = 0.5;
                ctx.drawImage(img, areaX, areaY, areaW, areaH);
                ctx.globalAlpha = 1;
                resolve();
            };
            img.onerror = () => resolve(); // skip if image fails to load
            img.src = floorPlan.imageUrl ?? floorPlan.imageData ?? '';
        });
    }

    if (floorPlan.rooms.length === 0) {
        ctx.fillStyle = '#9ca3af';
        ctx.font = '14px system-ui, sans-serif';
        ctx.fillText('No zones or tables', areaX + 20, areaY + 40);
    } else {
        const maxX = Math.max(...floorPlan.rooms.map(r => r.position.x + r.width));
        const maxY = Math.max(...floorPlan.rooms.map(r => r.position.y + r.height));
        const scaleX = (areaW - PAD * 2) / (maxX || 1);
        const scaleY = (areaH - PAD * 2) / (maxY || 1);
        const scale = Math.min(scaleX, scaleY, 1);

        const roomTypeColors: Record<string, string> = {
            ceremony: '#3b82f6', reception: '#10b981', cocktail: '#f59e0b',
            dining: '#8b5cf6', dance: '#ef4444', stage: '#f97316',
            entrance: '#06b6d4', restroom: '#64748b', kitchen: '#84cc16',
            table: '#a3e635', other: '#6b7280',
        };

        const zones = floorPlan.rooms.filter(r => r.type !== 'table');
        const tables = floorPlan.rooms.filter(r => r.type === 'table');

        for (const room of zones) {
            const rx = areaX + PAD + room.position.x * scale;
            const ry = areaY + PAD + room.position.y * scale;
            const rw = room.width * scale;
            const rh = room.height * scale;
            const color = roomTypeColors[room.type] ?? (presetFor(room.type) ? resolveToken(presetFor(room.type)!.tone) : roomTypeColors.other);
            ctx.globalAlpha = 0.6;
            ctx.fillStyle = color;
            ctx.fillRect(rx, ry, rw, rh);
            ctx.globalAlpha = 1;
            ctx.strokeStyle = color;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(rx, ry, rw, rh);
            ctx.fillStyle = '#fff';
            ctx.font = `${Math.max(8, Math.min(11, rw / 6))}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(room.name, rx + rw / 2, ry + rh / 2 + 4);
            ctx.textAlign = 'left';
        }

        for (const room of tables) {
            const shape = room.tableShape ?? 'round';
            const rx = areaX + PAD + room.position.x * scale;
            const ry = areaY + PAD + room.position.y * scale;
            const rw = room.width * scale;
            const rh = room.height * scale;
            const color = room.groupColor ?? roomTypeColors.table;

            ctx.fillStyle = color;
            if (shape === 'crescent') {
                const facing = crescentFacing(room, floorPlan.rooms);
                const pts = crescentOutline({ x: rx, y: ry, width: rw, height: rh }, facing);
                ctx.beginPath();
                pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
                ctx.closePath();
                ctx.fill();
                if (room.isHeadTable) {
                    ctx.strokeStyle = resolveToken('--warning');
                    ctx.lineWidth = 2.5;
                    ctx.stroke();
                }
            } else if (shape === 'round' || shape === 'oval') {
                ctx.beginPath();
                ctx.ellipse(rx + rw / 2, ry + rh / 2, rw / 2, rh / 2, 0, 0, Math.PI * 2);
                ctx.fill();
                if (room.isHeadTable) {
                    ctx.strokeStyle = '#f59e0b';
                    ctx.lineWidth = 2.5;
                    ctx.stroke();
                }
            } else {
                ctx.fillRect(rx, ry, rw, rh);
                if (room.isHeadTable) {
                    ctx.strokeStyle = '#f59e0b';
                    ctx.lineWidth = 2.5;
                    ctx.strokeRect(rx, ry, rw, rh);
                }
            }
            ctx.fillStyle = '#fff';
            ctx.font = `bold ${Math.max(7, Math.min(10, rw / 3))}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            // a crescent is hollow in the middle, so its number goes on the solid arc
            const labelAt = shape === 'crescent'
                ? crescentLabelPoint({ x: rx, y: ry, width: rw, height: rh }, crescentFacing(room, floorPlan.rooms))
                : { x: rx + rw / 2, y: ry + rh / 2 };
            ctx.fillText(labelForRoom(room, floorPlan.rooms), labelAt.x, labelAt.y + 3);
            ctx.textAlign = 'left';
        }
    }

    const link = document.createElement('a');
    link.download = `floor-plan-${floorPlan.name.replace(/\s+/g, '-').toLowerCase()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
}

function venueToForm(v: VenueData): VenueForm {
    return {
        name: v.name,
        address: v.address,
        capacity: String(v.capacity),
        phone: v.contactInfo.phone ?? '',
        email: v.contactInfo.email ?? '',
        website: v.contactInfo.website ?? '',
        notes: (v as VenueData & { notes?: string }).notes ?? '',
        amenities: v.amenities.join(', '),
        capacitySeatedBanquet: v.capacitySeatedBanquet != null ? String(v.capacitySeatedBanquet) : '',
        capacitySeatedConference: v.capacitySeatedConference != null ? String(v.capacitySeatedConference) : '',
        capacityStanding: v.capacityStanding != null ? String(v.capacityStanding) : '',
    };
}

const VenueMetrics: React.FC<{ venues: number; capacity: number; plans: number; spots: number }> = ({ venues, capacity, plans, spots }) => (
    <section className="stats-grid" aria-label="Venues summary">
        <MetricCard label="Venues" value={venues} icon="mapPin" tone="pink" />
        <MetricCard label="Total capacity" value={capacity.toLocaleString()} icon="users" tone="teal" />
        <MetricCard label="Floor plans" value={plans} icon="document" tone="blue" />
        <MetricCard label="Spots" value={spots} icon="grid" tone="purple" />
    </section>
);

/** Floor plans are often named "<Venue>: <Event>"; the card title already says the venue. */
export function floorPlanLabel(plan: string, venue: string): string {
    const rest = plan.startsWith(venue) ? plan.slice(venue.length).replace(/^\s*[:—–-]\s*/, '') : plan;
    return rest.trim() || plan;
}

export const VenueManager: React.FC<VenueManagerProps> = ({ venueId, onBreadcrumbState, statusFilter = 'active' }) => {
  // Re-render when the record numbers (PO-130 instead of PO-90F0D6B4) load.
  useRecordRefs();
    const { canEdit } = useRole();
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();
    const [venues, setVenues] = useState<VenueData[]>([]);
    const [selectedVenue, setSelectedVenue] = useState<VenueData | null>(null);
    const [selectedFloorPlan, setSelectedFloorPlan] = useState<FloorPlan | null>(null);
    const [form, setForm] = useState<VenueForm | null>(null);
    const [saved, setSaved] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
    const [zoneListOpen, setZoneListOpen] = useState(true);
    const [zoneListPage, setZoneListPage] = useState(0);
    const [zoneType, setZoneType] = useState<Room['type']>('other');
    const [seatingTables, setSeatingTables] = useState<SeatingTable[]>([]);
    const [tableEdit, setTableEdit] = useState<{
        num: string;
        label: string;
        cap: string;
        zone: string;
        groupId: string;
        isHeadTable: boolean;
    } | null>(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [savedTemplates, setSavedTemplates] = useState<FloorPlanTemplate[]>([]);
    const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
    const [saveTemplateName, setSaveTemplateName] = useState('');
    const [saveTemplateDesc, setSaveTemplateDesc] = useState('');
    const [summaryCopied, setSummaryCopied] = useState(false);

    // Guest groups + guests for named seat slots
    const [guestGroups, setGuestGroups] = useState<GuestGroup[]>([]);
    const [guests, setGuests] = useState<Guest[]>([]);
    const [sharedFloorPlan, setSharedFloorPlan] = useState<FloorPlan | null>(null);
    const [venuePhotos, setVenuePhotos] = useState<string[]>([]);
    const [galleryIdx, setGalleryIdx] = useState(0);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const capacityInputRef = useRef<HTMLInputElement>(null);

    // Table mini-panel ref for scroll
    const editTableRef = useRef<HTMLDivElement>(null);

    // Tracks rooms as of last DB save per floor plan id (for diff-based zone sync)
    const savedRoomsRef = useRef<Map<string, import('../types/Planner').Room[]>>(new Map());

    // GAP 3: Undo/Redo history
    const historyRef = useRef<FloorPlan[]>([]);
    const redoRef = useRef<FloorPlan[]>([]);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [venueInfoOpen, setVenueInfoOpen] = useState(true);
    const [contactOpen, setContactOpen] = useState(false);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [loading, setLoading] = useState(true);
    const [reloadTick, setReloadTick] = useState(0);
    const search = useHeaderSearch().trim().toLowerCase();

    const dispatchHistoryChanged = useCallback((undoLen: number, redoLen: number) => {
        const cu = undoLen > 0;
        const cr = redoLen > 0;
        setCanUndo(cu);
        setCanRedo(cr);
        window.dispatchEvent(new CustomEvent('floor-plan-history-changed', { detail: { canUndo: cu, canRedo: cr } }));
    }, []);

    // Load venues; auto-select by venueId prop (detail view) or leave unselected (list view)
    useEffect(() => {
        const cached = getCachedVenues();
        const init = (loadedVenues: import('../types/Planner').VenueData[]) => {
            setVenues(loadedVenues);
            // Seed savedRoomsRef from DB state
            for (const v of loadedVenues) {
                for (const fp of v.floorPlans) {
                    savedRoomsRef.current.set(fp.id, fp.rooms);
                }
            }
            if (venueId) {
                const v = loadedVenues.find(lv => lv.id === venueId) ?? null;
                if (v) {
                    setSelectedVenue(v);
                    setForm(venueToForm(v));
                    if (v.floorPlans.length > 0) setSelectedFloorPlan(v.floorPlans[0]);
                }
            }
        };
        if (cached.length > 0) {
            init(cached);
        }
        setLoadError(null);
        setLoading(true);
        loadVenuesFromApi()
            .then(init)
            .catch(err => setLoadError(err))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueId, reloadTick]);

    // Listen for header venue-select events
    useEffect(() => {
        const handler = (e: Event) => {
            const id = (e as CustomEvent<string>).detail;
            const found = venues.find(v => v.id === id);
            if (found) selectVenue(found);
        };
        window.addEventListener('venue-select', handler);
        return () => window.removeEventListener('venue-select', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venues]);

    // Load seating tables once for zone detail panel
    useEffect(() => {
        void fetchSeatingTables().then(setSeatingTables);
    }, []);


    // Load guest groups and guests on mount
    useEffect(() => {
        setGuestGroups(loadGuestGroups());
        setGuests(loadGuests());
    }, []);

    // Keep breadcrumb clean
    useEffect(() => {
        onBreadcrumbState?.({ venueName: null, floorPlanName: null });
    }, [onBreadcrumbState]);

    // Broadcast active venue name + floor plan name + zone type to Header
    useEffect(() => {
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        window.dispatchEvent(new CustomEvent('venue-fp-state', {
            detail: {
                venueName: selectedVenue?.name ?? null,
                fpName: fp?.name ?? null,
                zoneType,
            },
        }));
    }, [selectedFloorPlan, zoneType, selectedVenue]);

    // Keep local zoneType in sync when Header's select changes
    useEffect(() => {
        const handler = (e: Event) => setZoneType((e as CustomEvent<Room['type']>).detail);
        window.addEventListener('venue-fp-zone-type', handler);
        return () => window.removeEventListener('venue-fp-zone-type', handler);
    }, []);

    // Export button (Header): the venues this list shows (nothing on a single venue's page).
    useExport('venues', {
        rows: venueId ? [] : (statusFilter === 'all'
            ? venues
            : venues.filter(v => (statusFilter === 'archived' ? !!v.archived : !v.archived))),
        ctx: undefined,
    });

    // Shareable link: serialize floor plan to URL hash
    useEffect(() => {
        const handler = () => {
            const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
            if (!fp) return;
            try {
                const json = JSON.stringify(fp);
                const b64 = btoa(unescape(encodeURIComponent(json)));
                const url = `${window.location.origin}${window.location.pathname}#fp=${b64}`;
                void navigator.clipboard.writeText(url).then(() => {
                    showSuccess('Link copied to clipboard');
                });
            } catch {
                showError('Could not generate link');
            }
        };
        window.addEventListener('venue-copy-link', handler);
        return () => window.removeEventListener('venue-copy-link', handler);
    }, [selectedFloorPlan, selectedVenue, showSuccess, showError]);

    // Shareable link: read a #fp= hash on mount and show it read-only.
    // Runs once — a shared link is a one-time entry point, not a live subscription.
    useEffect(() => {
        const hash = window.location.hash;
        const match = hash.match(/#fp=(.+)$/);
        if (!match) return;
        try {
            const json = decodeURIComponent(escape(atob(match[1])));
            const fp = JSON.parse(json) as FloorPlan;
            if (!fp || !Array.isArray(fp.rooms)) throw new Error('malformed floor plan');
            setSharedFloorPlan(fp);
        } catch {
            showError('This shared link is invalid or corrupted');
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const closeSharedFloorPlan = () => {
        setSharedFloorPlan(null);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
    };

    // GAP 3: Undo/Redo keyboard shortcuts & custom events
    useEffect(() => {
        const onUndo = () => {
            if (historyRef.current.length === 0) return;
            const activeFp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
            if (!activeFp) return;
            const prev = historyRef.current.pop()!;
            redoRef.current.push(activeFp);
            applyFloorPlanDirectly(prev);
            dispatchHistoryChanged(historyRef.current.length, redoRef.current.length);
        };
        const onRedo = () => {
            if (redoRef.current.length === 0) return;
            const activeFp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
            if (!activeFp) return;
            const next = redoRef.current.pop()!;
            historyRef.current.push(activeFp);
            applyFloorPlanDirectly(next);
            dispatchHistoryChanged(historyRef.current.length, redoRef.current.length);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); onUndo(); }
                if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); onRedo(); }
            }
        };
        window.addEventListener('floor-plan-undo', onUndo);
        window.addEventListener('floor-plan-redo', onRedo);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('floor-plan-undo', onUndo);
            window.removeEventListener('floor-plan-redo', onRedo);
            window.removeEventListener('keydown', onKey);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFloorPlan, selectedVenue]);

    // Sync table edit fields when a table room is selected/deselected
    useEffect(() => {
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        if (!fp || !selectedRoomId) { setTableEdit(null); return; }
        const room = fp.rooms.find(r => r.id === selectedRoomId);
        if (room?.type === 'table') {
            setTableEdit({
                num: room.name.replace('Table ', ''),
                label: room.assignedPurpose ?? '',
                cap: room.seats != null ? String(room.seats) : room.capacity != null ? String(room.capacity) : '',
                zone: room.section ?? '',
                groupId: room.groupId ?? '',
                isHeadTable: room.isHeadTable ?? false,
            });
        } else {
            setTableEdit(null);
        }
    }, [selectedRoomId, selectedFloorPlan, selectedVenue]);

    const selectVenue = (venue: VenueData) => {
        setSelectedVenue(venue);
        setForm(venueToForm(venue));
        setSelectedFloorPlan(venue.floorPlans[0] ?? null);
        setSelectedRoomId(null);
        setGalleryIdx(0);
        const stored = localStorage.getItem(`vm_photos_${venue.id}`);
        setVenuePhotos(stored ? JSON.parse(stored) : (venue.photos ?? []));
        // Reset undo/redo on venue switch
        historyRef.current = [];
        redoRef.current = [];
        dispatchHistoryChanged(0, 0);
        window.dispatchEvent(new CustomEvent('floor-plan-add-room', { detail: false }));
    };

    const handleField = (field: keyof VenueForm, value: string) => {
        setForm(prev => prev ? { ...prev, [field]: value } : prev);
        setSaved(false);
    };

    const handleSave = useCallback(async () => {
        if (!canEdit || !selectedVenue || !form) return;
        if (!form.name.trim()) { showError('Name is required.'); return; }
        const updated: VenueData = {
            ...selectedVenue,
            name: form.name.trim(),
            address: form.address,
            capacity: parseInt(form.capacity) || selectedVenue.capacity,
            contactInfo: {
                phone: form.phone || undefined,
                email: form.email || undefined,
                website: form.website || undefined,
            },
            amenities: form.amenities.split(',').map(s => s.trim()).filter(Boolean),
            capacitySeatedBanquet: form.capacitySeatedBanquet ? parseInt(form.capacitySeatedBanquet) : undefined,
            capacitySeatedConference: form.capacitySeatedConference ? parseInt(form.capacitySeatedConference) : undefined,
            capacityStanding: form.capacityStanding ? parseInt(form.capacityStanding) : undefined,
        };
        await apiSaveVenueDetails(updated);

        // Also persist the active floor plan (rooms may have changed via drag/resize since last save)
        const fp = selectedFloorPlan ?? selectedVenue.floorPlans[0] ?? null;
        if (fp) {
            const prevRooms = savedRoomsRef.current.get(fp.id) ?? [];
            try {
                const savedFp = await apiSaveFloorPlan(selectedVenue.id, fp, prevRooms);
                savedRoomsRef.current.set(savedFp.id, savedFp.rooms);
                const finalVenue: VenueData = {
                    ...updated,
                    floorPlans: updated.floorPlans.map(p =>
                        p.id === fp.id || p.id === savedFp.id ? savedFp : p
                    ),
                };
                setSelectedVenue(finalVenue);
                setVenues(prev => prev.map(v => v.id === finalVenue.id ? finalVenue : v));
                if (selectedFloorPlan?.id === fp.id) setSelectedFloorPlan(savedFp);
                // Event Day's table copy is a one-way sync from floor plans, not
                // a live read — push it a fresh-sync signal now instead of
                // leaving it to catch up on its own multi-minute throttle.
                window.dispatchEvent(new CustomEvent('floor-plan-tables-changed'));
            } catch {
                setSelectedVenue(updated);
                setVenues(prev => prev.map(v => v.id === updated.id ? updated : v));
                // savedRoomsRef is deliberately left untouched here: the next save's
                // prevRooms diff must still see the unsaved zones as pending.
                showError('Venue details saved, but the floor plan could not be saved. Please try again.');
                return;
            }
        } else {
            setSelectedVenue(updated);
            setVenues(prev => prev.map(v => v.id === updated.id ? updated : v));
        }

        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        showSuccess('Venue saved');
    }, [canEdit, selectedVenue, form, selectedFloorPlan, showSuccess, showError]);

    const setArchivedStatus = useCallback(async (nextArchived: boolean) => {
        if (!canEdit || !selectedVenue || selectedVenue.archived === nextArchived) return;
        try {
            await apiSetVenueArchived(selectedVenue.id, nextArchived);
            const updated = { ...selectedVenue, archived: nextArchived };
            setSelectedVenue(updated);
            setVenues(prev => prev.map(v => v.id === updated.id ? updated : v));
            showSuccess(nextArchived ? 'Venue archived' : 'Venue unarchived');
        } catch {
            showError('Failed to update venue status');
        }
    }, [canEdit, selectedVenue, showSuccess, showError]);

    // Delete forever is only offered on an archived venue (Header shows the
    // button only then); the dialog says why if the delete is refused.
    const foreverVenue = useDeleteConfirm<{ id: string; name: string }>({
        record: 'venue',
        perform: v => apiDeleteVenue(v.id),
        onDone: v => {
            setVenues(prev => prev.filter(x => x.id !== v.id));
            setSelectedVenue(null);
            showSuccess('Venue deleted');
        },
    });

    // Header shows the Active/Archived toggle + Delete for the selected venue
    // (moved out of the sidebar) — bridge state via CustomEvents like the
    // guest/fabric detail pages do, since VenueManager has no header props.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('venue-status-sync', { detail: !!selectedVenue?.archived }));
    }, [selectedVenue?.id, selectedVenue?.archived]);

    useEffect(() => {
        const onSet = (e: Event) => { void setArchivedStatus((e as CustomEvent<boolean>).detail); };
        const onDelete = () => { if (canEdit && canDeleteForever(selectedVenue)) foreverVenue.request({ id: selectedVenue!.id, name: selectedVenue!.name }); };
        window.addEventListener('venue-status-set', onSet);
        window.addEventListener('venue-delete-trigger', onDelete);
        return () => {
            window.removeEventListener('venue-status-set', onSet);
            window.removeEventListener('venue-delete-trigger', onDelete);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEdit, setArchivedStatus, selectedVenue]);

    // GAP 3: direct apply without pushing to history (used by undo/redo)
    const applyFloorPlanDirectly = useCallback((updatedFloorPlan: FloorPlan) => {
        if (!selectedVenue) return;
        const updatedVenue = {
            ...selectedVenue,
            floorPlans: selectedVenue.floorPlans.map(fp =>
                fp.id === updatedFloorPlan.id ? updatedFloorPlan : fp
            ),
        };
        setSelectedVenue(updatedVenue);
        setSelectedFloorPlan(updatedFloorPlan);
        setVenues(prev => prev.map(v => v.id === updatedVenue.id ? updatedVenue : v));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVenue]);

    const handleFloorPlanUpdate = useCallback((updatedFloorPlan: FloorPlan) => {
        if (!canEdit || !selectedVenue) return;
        // GAP 3: push to undo history before applying
        const activeFp = selectedFloorPlan ?? selectedVenue.floorPlans[0] ?? null;
        if (activeFp) {
            historyRef.current = [...historyRef.current.slice(-49), activeFp]; // cap at 50
            redoRef.current = [];
            dispatchHistoryChanged(historyRef.current.length, 0);
        }
        const updatedVenue = {
            ...selectedVenue,
            floorPlans: selectedVenue.floorPlans.map(fp =>
                fp.id === updatedFloorPlan.id ? updatedFloorPlan : fp
            ),
        };
        setSelectedVenue(updatedVenue);
        setSelectedFloorPlan(updatedFloorPlan);
        setVenues(prev => prev.map(v => v.id === updatedVenue.id ? updatedVenue : v));
    }, [canEdit, selectedVenue, selectedFloorPlan, dispatchHistoryChanged]);

    const handleRoomAdd = (roomData: Omit<Room, 'id'>) => {
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        if (!fp) return;
        const newRoom: Room = { ...roomData, id: `room-${Date.now()}` };
        handleFloorPlanUpdate({ ...fp, rooms: [...fp.rooms, newRoom] });
    };

    const handleRoomUpdate = (roomId: string, updates: Partial<Room>) => {
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        if (!fp) return;

        let finalUpdates = { ...updates };

        if (updates.section !== undefined && updates.section !== '') {
            const zone = fp.rooms.find(r => r.type !== 'table' && r.name === updates.section);
            if (zone) {
                const tablesAlreadyInZone = fp.rooms.filter(
                    r => r.type === 'table' && r.section === updates.section && r.id !== roomId
                );
                const TABLE_SIZE = 60;
                const PADDING = 12;
                const COLS = Math.max(1, Math.floor((zone.width - PADDING) / (TABLE_SIZE + PADDING)));
                const idx = tablesAlreadyInZone.length;
                const col = idx % COLS;
                const row = Math.floor(idx / COLS);
                finalUpdates.position = {
                    x: zone.position.x + PADDING + col * (TABLE_SIZE + PADDING),
                    y: zone.position.y + PADDING + row * (TABLE_SIZE + PADDING),
                };
            }
        }

        handleFloorPlanUpdate({
            ...fp,
            rooms: fp.rooms.map(r => r.id === roomId ? { ...r, ...finalUpdates } : r),
        });
    };

    // GAP 9: Auto-resequence tables after remove
    const handleRoomRemove = (roomId: string) => {
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        if (!fp) return;
        const remaining = fp.rooms.filter(r => r.id !== roomId);

        // Auto-resequence if all table names are numeric
        const tables = remaining.filter(r => r.type === 'table');
        const allNumeric = tables.length > 0 && tables.every(t => /^\d+$/.test(t.name));
        const resequenced = allNumeric
            ? (() => {
                const sorted = [...tables].sort((a, b) =>
                    (a.position.y + a.position.x) - (b.position.y + b.position.x)
                );
                const numMap = new Map(sorted.map((t, i) => [t.id, String(i + 1)]));
                return remaining.map(r => r.type === 'table' ? { ...r, name: numMap.get(r.id) ?? r.name } : r);
            })()
            : remaining;

        handleFloorPlanUpdate({ ...fp, rooms: resequenced });
    };

    const handleGroupDrop = (groupId: string, roomId: string) => {
        const group = guestGroups.find(g => g.id === groupId);
        if (!group) return;
        handleRoomUpdate(roomId, {
            groupId: group.id,
            groupColor: group.color,
            groupName: group.name,
        });
    };

    const handleGroupUnassign = (roomId: string) => {
        handleRoomUpdate(roomId, { groupId: undefined, groupColor: undefined, groupName: undefined });
    };

    const handleAddFloorPlan = () => {
        if (!canEdit) return;
        setShowTemplateModal(true);
        loadFloorPlanTemplates().then(setSavedTemplates).catch(() => setSavedTemplates([]));
    };

    const createFloorPlanFromTemplate = async (template: string) => {
        if (!selectedVenue) return;
        setShowTemplateModal(false);

        const savedTemplate = template !== 'blank' ? savedTemplates.find(t => t.id === template) : null;
        const templateRooms = savedTemplate?.rooms ?? [];
        const templateName = savedTemplate?.name ?? `Floor Plan ${selectedVenue.floorPlans.length + 1}`;

        const newFp: FloorPlan = {
            id: `fp-new-${Date.now()}`,
            venueId: selectedVenue.id,
            name: templateName,
            rooms: templateRooms.map((r, i) => ({ ...r, id: `room-new-${Date.now()}-${i}` })),
            scale: savedTemplate?.scale ?? 10,
            dimensions: { width: savedTemplate?.width ?? 1000, height: savedTemplate?.height ?? 700 },
        };

        try {
            const savedFp = await apiSaveFloorPlan(selectedVenue.id, newFp, []);
            savedRoomsRef.current.set(savedFp.id, savedFp.rooms);
            const updated: VenueData = { ...selectedVenue, floorPlans: [...selectedVenue.floorPlans, savedFp] };
            setSelectedVenue(updated);
            setVenues(prev => prev.map(v => v.id === updated.id ? updated : v));
            setSelectedFloorPlan(savedFp);
            window.dispatchEvent(new CustomEvent('floor-plan-tables-changed'));
        } catch {
            showError('Failed to create floor plan. Please try again.');
        }
    };

    useEffect(() => {
        window.addEventListener('venue-save-trigger', handleSave);
        return () => window.removeEventListener('venue-save-trigger', handleSave);
    }, [handleSave]);

    const handleSaveAsTemplateClick = useCallback(() => {
        if (!canEdit) return;
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        if (!fp || fp.rooms.length === 0) {
            showError('This floor plan has no rooms to save as a template.');
            return;
        }
        setSaveTemplateName(fp.name);
        setSaveTemplateDesc('');
        setShowSaveTemplateModal(true);
    }, [canEdit, selectedFloorPlan, selectedVenue, showError]);

    const handleConfirmSaveTemplate = useCallback(async () => {
        if (!canEdit) return;
        const fp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
        if (!fp || !saveTemplateName.trim()) return;
        try {
            await apiCreateFloorPlanTemplate({
                name: saveTemplateName.trim(),
                description: saveTemplateDesc.trim() || undefined,
                rooms: fp.rooms.map(({ id: _id, ...rest }) => rest),
                scale: fp.scale,
                width: fp.dimensions.width,
                height: fp.dimensions.height,
            });
            setShowSaveTemplateModal(false);
            showSuccess('Saved as template');
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to save template');
        }
    }, [canEdit, selectedFloorPlan, selectedVenue, saveTemplateName, saveTemplateDesc, showSuccess, showError]);

    useEffect(() => {
        window.addEventListener('floor-plan-save-template-trigger', handleSaveAsTemplateClick);
        return () => window.removeEventListener('floor-plan-save-template-trigger', handleSaveAsTemplateClick);
    }, [handleSaveAsTemplateClick]);

    const [confirmDeleteTemplate, setConfirmDeleteTemplate] = useState<{ id: string; name: string } | null>(null);

    const handleDeleteTemplate = useCallback(async (id: string) => {
        setConfirmDeleteTemplate(null);
        try {
            await apiDeleteFloorPlanTemplate(id);
            setSavedTemplates(prev => prev.filter(t => t.id !== id));
        } catch {
            showError('Failed to delete template');
        }
    }, [showError]);

    const handlePhotoUpload = async (files: FileList | null) => {
        if (!canEdit) return;
        if (!files || !selectedVenue) return;
        const newUrls: string[] = [];
        for (const file of Array.from(files)) {
            try {
                const result = await uploadImage(file, { entityType: 'venue', entityId: selectedVenue.id });
                newUrls.push(result.url);
            } catch {
                showError(`Failed to upload ${file.name}`);
            }
        }
        if (!newUrls.length) return;
        const updated = [...venuePhotos, ...newUrls];
        setVenuePhotos(updated);
        setGalleryIdx(updated.length - 1);
        localStorage.setItem(`vm_photos_${selectedVenue.id}`, JSON.stringify(updated));
        showSuccess(`${newUrls.length} photo${newUrls.length > 1 ? 's' : ''} uploaded`);
    };

    const removePhoto = (idx: number) => {
        if (!selectedVenue) return;
        const updated = venuePhotos.filter((_, i) => i !== idx);
        setVenuePhotos(updated);
        setGalleryIdx(Math.min(galleryIdx, Math.max(0, updated.length - 1)));
        localStorage.setItem(`vm_photos_${selectedVenue.id}`, JSON.stringify(updated));
    };

    // GAP 2: Compute guest group counts (guests per groupId) using GuestGroup.guestIds
    const guestGroupCounts = useCallback((): Record<string, number> => {
        const counts: Record<string, number> = {};
        for (const grp of guestGroups) {
            counts[grp.id] = grp.guestIds.length;
        }
        return counts;
    }, [guestGroups]);

    // GAP 2: Compute fill status map using both namedSeats (head tables) and group guest counts
    const tableStatusMap = useCallback((fp: FloorPlan, groupCounts: Record<string, number>): Record<string, 'empty' | 'partial' | 'full'> => {
        const map: Record<string, 'empty' | 'partial' | 'full'> = {};
        for (const room of fp.rooms) {
            if (room.type !== 'table') continue;
            const cap = room.seats ?? room.capacity;
            if (!cap) continue;
            let filled: number;
            if (room.isHeadTable || !room.groupId) {
                filled = (room.namedSeats ?? []).filter(s => s.guestName.trim()).length;
            } else {
                filled = groupCounts[room.groupId] ?? 0;
            }
            if (filled === 0) map[room.id] = 'empty';
            else if (filled >= cap) map[room.id] = 'full';
            else map[room.id] = 'partial';
        }
        return map;
    }, []);

    // List view
    if (!venueId) {
        const byStatus = statusFilter === 'all'
            ? venues
            : venues.filter(v => statusFilter === 'archived' ? !!v.archived : !v.archived);
        const filteredVenues = byStatus.filter(v =>
            !search || [v.name, v.address, ...v.amenities].some(t => t?.toLowerCase().includes(search)));
        const live = venues.filter(v => !v.archived);
        const metrics = {
            venues: live.length,
            capacity: live.reduce((sum, v) => sum + (v.capacity || 0), 0),
            plans: live.reduce((sum, v) => sum + v.floorPlans.length, 0),
            spots: live.reduce((sum, v) => sum + v.floorPlans.reduce((n, fp) => n + fp.rooms.length, 0), 0),
        };
        if (loadError) {
            return (
                <div className="wt-container fade-in">
                    <LoadErrorBox what="venues" error={loadError} retrying={loading} onRetry={() => setReloadTick(t => t + 1)} />
                </div>
            );
        }
        if (venues.length === 0) {
            return loading ? <div className="wt-container fade-in" /> : (
                <div className="wt-container fade-in">
                    <EmptyState
                        icon="mapPin"
                        title="No venues yet"
                        description="Add a venue to start planning your floor layout"
                    />
                </div>
            );
        }
        if (filteredVenues.length === 0) {
            return (
                <div className="wt-container fade-in">
                    {statusFilter !== 'archived' && <VenueMetrics {...metrics} />}
                    <EmptyState
                        icon="mapPin"
                        title={search ? 'No venues match your search' : statusFilter === 'archived' ? 'No archived venues' : 'No active venues'}
                        description={search ? undefined : statusFilter === 'archived' ? 'Venues you archive will show up here.' : 'All your venues are archived — switch the filter above to see them.'}
                    />
                </div>
            );
        }
        return (
            <div className="wt-container fade-in vm-relative">
                <RecordListPublisher kind="venue" ids={filteredVenues.map(v => v.id)} />
                {statusFilter !== 'archived' && <VenueMetrics {...metrics} />}
                <div className="vm-venue-list">
                    {filteredVenues.map(v => {
                        const totalZones = v.floorPlans.reduce((sum, fp) => sum + fp.rooms.length, 0);
                        const dims = v.floorPlans[0]?.dimensions;
                        const footerStats: { icon: string; label: string }[] = [
                            { icon: 'users', label: v.capacity > 0 ? `${v.capacity.toLocaleString()} pax` : '— pax' },
                            { icon: 'document', label: `${v.floorPlans.length} floor plan${v.floorPlans.length !== 1 ? 's' : ''}` },
                            { icon: 'grid', label: `${totalZones} spot${totalZones !== 1 ? 's' : ''}` },
                        ];
                        if (dims) {
                            footerStats.push({ icon: 'ruler', label: `${dims.width} × ${dims.height} ft` });
                        }
                        const details: { label: string; value: string }[] = [
                            { label: 'ID', value: recordLabel('venue', v.id) },
                        ];
                        if (v.amenities.length > 0) {
                            details.push({ label: 'Amenities', value: v.amenities.join(' · ') });
                        }
                        return (
                            <ContentCard
                                key={v.id}
                                hideImage
                                title={v.name}
                                subtitle={v.address}
                                status={v.archived ? { label: 'Archived', type: 'inactive' } : undefined}
                                details={details}
                                tags={v.floorPlans.map(fp => floorPlanLabel(fp.name, v.name))}
                                footerStats={footerStats}
                                onClick={() => navigate(entityPath('venue', v.id) ?? '/venues')}
                            />
                        );
                    })}
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="wt-container fade-in">
                <LoadErrorBox what="this venue" error={loadError} retrying={loading} onRetry={() => setReloadTick(t => t + 1)} />
            </div>
        );
    }
    if (!selectedVenue) {
        return loading ? <div className="wt-container fade-in" /> : (
            <div className="wt-container fade-in">
                <EmptyState icon="mapPin" title="Venue not found" description="It may have been deleted. Go back to Venues and pick another." />
            </div>
        );
    }

    const activeFp = selectedFloorPlan ?? selectedVenue?.floorPlans[0] ?? null;
    const groupCounts = guestGroupCounts();
    const statusMap = activeFp ? tableStatusMap(activeFp, groupCounts) : {};

    // GAP 6: Zone capacity analysis
    const getZoneStats = (zoneName: string, fp: FloorPlan): { tableCount: number; totalSeats: number } => {
        const zoneTables = fp.rooms.filter(r => r.type === 'table' && r.section === zoneName);
        const totalSeats = zoneTables.reduce((sum, t) => sum + (t.seats ?? t.capacity ?? 0), 0);
        return { tableCount: zoneTables.length, totalSeats };
    };

    // GAP 7: Groups that have tables in the active floor plan
    const getGroupsWithTables = (fp: FloorPlan): Array<{ group: GuestGroup; tableCount: number; totalSeats: number; guestCount: number }> => {
        const result: Array<{ group: GuestGroup; tableCount: number; totalSeats: number; guestCount: number }> = [];
        for (const group of guestGroups) {
            const groupTables = fp.rooms.filter(r => r.type === 'table' && r.groupId === group.id);
            if (groupTables.length === 0) continue;
            const totalSeats = groupTables.reduce((sum, t) => sum + (t.seats ?? t.capacity ?? 0), 0);
            const guestCount = groupCounts[group.id] ?? 0;
            result.push({ group, tableCount: groupTables.length, totalSeats, guestCount });
        }
        return result;
    };

    return (
        <>
        <div className="wt-container fade-in">
                <div className="detail-layout">
                    {/* Main — floor plan canvas */}
                    <div className="detail-main">
                        {selectedVenue && (
                            <>
                                {selectedVenue.floorPlans.length > 1 && (
                                    <div className="vm-fp-tabs">
                                        {selectedVenue.floorPlans.map(fp => (
                                            <button
                                                key={fp.id}
                                                className={`vm-fp-tab${activeFp?.id === fp.id ? ' active' : ''}`}
                                                onClick={() => setSelectedFloorPlan(fp)}
                                            >
                                                {fp.name}
                                                <span className="vm-fp-zone-count">{fp.rooms.length}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {activeFp ? (
                                    <div className="vm-fp-block vm-fp-block--with-panel">
                                        <FloorPlanEditorFabric
                                            embedded
                                            venue={selectedVenue}
                                            floorPlan={activeFp}
                                            onFloorPlanUpdate={handleFloorPlanUpdate}
                                            onRoomAdd={handleRoomAdd}
                                            onRoomUpdate={handleRoomUpdate}
                                            onRoomRemove={handleRoomRemove}
                                            onRoomSelect={id => setSelectedRoomId(id)}
                                            externalSelectedRoomId={selectedRoomId}
                                            tableStatusMap={statusMap}
                                            guestGroupCounts={groupCounts}
                                            onGroupDrop={handleGroupDrop}
                                        />
                                        {guestGroups.length > 0 && (
                                            <GuestSeatingPanel
                                                groups={guestGroups}
                                                rooms={activeFp.rooms}
                                                onUnassignGroup={handleGroupUnassign}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <EmptyState
                                        icon="map"
                                        title="No floor plans yet"
                                        description={canEdit ? "Create your first floor plan to get started" : "No floor plan has been created for this venue yet."}
                                        action={canEdit ? (
                                            <button className="primary-btn" onClick={handleAddFloorPlan}>
                                                Create Floor Plan
                                            </button>
                                        ) : undefined}
                                    />
                                )}

                                {/* ── Venue Photo Gallery ─────────────────────────────── */}
                                <div className="vm-gallery-section">
                                    <div className="vm-gallery-header">
                                        <p className="form-section-title">Venue Photos</p>
                                        {canEdit && (
                                            <>
                                                <button
                                                    className="header-action-btn"
                                                    onClick={() => photoInputRef.current?.click()}
                                                    title="Upload venue photos"
                                                >
                                                    <Icon name="gallery" size={14} />
                                                    Upload photos
                                                </button>
                                                <input
                                                    ref={photoInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    className="u-hidden"
                                                    onChange={e => { void handlePhotoUpload(e.target.files); e.target.value = ''; }}
                                                />
                                            </>
                                        )}
                                    </div>
                                    {venuePhotos.length > 0 ? (
                                        <div className="vm-gallery">
                                            {/* Main viewer */}
                                            <div className="vm-gallery-main">
                                                <img
                                                    src={resolveMediaUrl(venuePhotos[galleryIdx])}
                                                    alt={`Venue photo ${galleryIdx + 1}`}
                                                    className="vm-gallery-main-img"
                                                />
                                                <button
                                                    className="vm-gallery-nav vm-gallery-nav-prev"
                                                    onClick={() => setGalleryIdx(i => (i - 1 + venuePhotos.length) % venuePhotos.length)}
                                                    disabled={venuePhotos.length <= 1}
                                                    title="Previous photo"
                                                ><Icon name="chevronLeft" size={18} /></button>
                                                <button
                                                    className="vm-gallery-nav vm-gallery-nav-next"
                                                    onClick={() => setGalleryIdx(i => (i + 1) % venuePhotos.length)}
                                                    disabled={venuePhotos.length <= 1}
                                                    title="Next photo"
                                                ><Icon name="chevronRight" size={18} /></button>
                                                <button
                                                    className="vm-gallery-remove"
                                                    onClick={() => removePhoto(galleryIdx)}
                                                    title="Remove this photo"
                                                ><Icon name="close" size={16} /></button>
                                                <span className="vm-gallery-count">{galleryIdx + 1} / {venuePhotos.length}</span>
                                            </div>
                                            {/* Thumbnail strip */}
                                            <div className="vm-gallery-strip">
                                                {venuePhotos.map((url, i) => (
                                                    <button
                                                        key={i}
                                                        className={`vm-gallery-thumb${i === galleryIdx ? ' active' : ''}`}
                                                        onClick={() => setGalleryIdx(i)}
                                                    >
                                                        <img src={resolveMediaUrl(url)} alt={`thumb ${i + 1}`} />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="vm-gallery-empty" onClick={() => photoInputRef.current?.click()}>
                                            <Icon name="gallery" size={24} />
                                            <p>Click to upload venue photos — exterior, interior, parking, rooms</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Sidebar — editable venue details */}
                    {selectedVenue && form && (
                        <div className="detail-sidebar">
                            <section className="side-section">
                                <button className="vm-collapse-header" onClick={() => setVenueInfoOpen(o => !o)}>
                                    <h4>Venue Info</h4>
                                    <Icon name={venueInfoOpen ? 'chevronUp' : 'chevronDown'} size={14} />
                                </button>
                                {venueInfoOpen && (
                                    <>
                                        <div className="vm-field">
                                            <label className="vm-label">Name<span className="form-required">*</span></label>
                                            <input className="form-input form-input--sm" value={form.name} onChange={e => handleField('name', e.target.value)} required />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Address</label>
                                            <input className="form-input form-input--sm" value={form.address} onChange={e => handleField('address', e.target.value)} />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Capacity</label>
                                            <input className="form-input form-input--sm" type="number" value={form.capacity} onChange={e => handleField('capacity', e.target.value)} />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Seated (Banquet)</label>
                                            <input className="form-input form-input--sm" type="number" value={form.capacitySeatedBanquet} onChange={e => handleField('capacitySeatedBanquet', e.target.value)} />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Seated (Conference)</label>
                                            <input className="form-input form-input--sm" type="number" value={form.capacitySeatedConference} onChange={e => handleField('capacitySeatedConference', e.target.value)} />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Standing</label>
                                            <input className="form-input form-input--sm" type="number" value={form.capacityStanding} onChange={e => handleField('capacityStanding', e.target.value)} />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Amenities <span className="form-label-hint">(comma-separated)</span></label>
                                            <input className="form-input form-input--sm" value={form.amenities} onChange={e => handleField('amenities', e.target.value)} placeholder="Parking, Catering Kitchen…" />
                                        </div>
                                    </>
                                )}
                            </section>

                            <section className="side-section">
                                <button className="vm-collapse-header" onClick={() => setContactOpen(o => !o)}>
                                    <h4>Contact</h4>
                                    <Icon name={contactOpen ? 'chevronUp' : 'chevronDown'} size={14} />
                                </button>
                                {contactOpen && (
                                    <>
                                        <div className="vm-field">
                                            <label className="vm-label">Phone</label>
                                            <input className="form-input form-input--sm" value={form.phone} onChange={e => handleField('phone', e.target.value)} placeholder={PHONE_PLACEHOLDER} />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Email</label>
                                            <input className="form-input form-input--sm" type="email" value={form.email} onChange={e => handleField('email', e.target.value)} placeholder="events@venue.com" />
                                        </div>
                                        <div className="vm-field">
                                            <label className="vm-label">Website</label>
                                            <input className="form-input form-input--sm" value={form.website} onChange={e => handleField('website', e.target.value)} placeholder="https://…" />
                                        </div>
                                    </>
                                )}
                            </section>

                            {activeFp && (() => {
                                const selectedRoom = selectedRoomId
                                    ? activeFp.rooms.find(r => r.id === selectedRoomId) ?? null
                                    : null;
                                const zoneTables = selectedRoom
                                    ? matchSection(selectedRoom.name, seatingTables)
                                    : [];

                                const namedSeatsCount = tableEdit?.cap ? Number(tableEdit.cap) : 0;

                                // GAP 6: Zone capacity stats for selected non-table zone
                                const zoneStats = selectedRoom && selectedRoom.type !== 'table'
                                    ? getZoneStats(selectedRoom.name, activeFp)
                                    : null;

                                return (
                                    <>
                                        <section className="side-section">
                                            <button className="vm-collapse-header" onClick={() => setZoneListOpen(o => !o)}>
                                                <h4>{activeFp.name}</h4>
                                                <div className="vm-fp-header-row">
                                                    <span className="vm-fp-zone-count">{activeFp.rooms.length} zones</span>
                                                    <Icon name={zoneListOpen ? 'chevronUp' : 'chevronDown'} size={14} />
                                                </div>
                                            </button>
                                            {zoneListOpen && (activeFp.rooms.length === 0 ? (
                                                <p className="vm-zones-empty">No zones yet — use Add zone in the toolbar above.</p>
                                            ) : (() => {
                                                const ZONES_PER_PAGE = 8;
                                                const zonePageCount = Math.ceil(activeFp.rooms.length / ZONES_PER_PAGE);
                                                const page = Math.min(zoneListPage, zonePageCount - 1);
                                                const pagedRooms = activeFp.rooms.slice(page * ZONES_PER_PAGE, (page + 1) * ZONES_PER_PAGE);
                                                return (
                                                <>
                                                <div className="vm-zone-list">
                                                    {pagedRooms.map(r => {
                                                        // GAP 6: Per-zone stats in sidebar list
                                                        const isZone = r.type !== 'table';
                                                        const zStats = isZone ? getZoneStats(r.name, activeFp) : null;
                                                        let capacityIndicator: string | null = null;
                                                        let indicatorColor: string | undefined;
                                                        if (isZone && zStats) {
                                                            if (r.capacity) {
                                                                const pct = zStats.totalSeats / r.capacity;
                                                                capacityIndicator = `${zStats.totalSeats}/${r.capacity} seats`;
                                                                indicatorColor = pct > 1 ? '#dc2626' : pct >= 0.9 ? '#16a34a' : '#f59e0b';
                                                            } else if (zStats.tableCount > 0) {
                                                                capacityIndicator = `${zStats.tableCount} tbl · ${zStats.totalSeats} seats`;
                                                            }
                                                        }
                                                        return (
                                                            <div
                                                                key={r.id}
                                                                className={`vm-zone-row${selectedRoomId === r.id ? ' vm-zone-row--selected' : ''}`}
                                                                onClick={() => setSelectedRoomId(r.id === selectedRoomId ? null : r.id)}
                                                            >
                                                                <span className="vm-zone-dot" style={{ /* ds-dynamic: zone type color from map */ background: ZONE_COLORS[r.type] ?? ZONE_COLORS.other }} />
                                                                <span className="vm-zone-name">{r.name}</span>
                                                                {/* GAP 6: capacity/table count suffix */}
                                                                {capacityIndicator && (
                                                                    <span className="vm-zone-cap" style={indicatorColor ? { color: indicatorColor, fontWeight: 600 } : undefined}>
                                                                        {capacityIndicator}
                                                                    </span>
                                                                )}
                                                                {!capacityIndicator && r.capacity && (
                                                                    <span className="vm-zone-cap">{r.capacity}</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {zonePageCount > 1 && (
                                                    <div className="ed-table-pagination">
                                                        <button className="ed-table-page-btn" onClick={() => setZoneListPage(p => Math.max(0, p - 1))} disabled={page === 0}>‹</button>
                                                        <span className="ed-table-page-label">{page + 1} / {zonePageCount}</span>
                                                        <button className="ed-table-page-btn" onClick={() => setZoneListPage(p => Math.min(zonePageCount - 1, p + 1))} disabled={page === zonePageCount - 1}>›</button>
                                                    </div>
                                                )}
                                                </>
                                                );
                                            })())}
                                        </section>

                                        {selectedRoom && selectedRoom.type === 'table' && tableEdit ? (
                                            <>
                                                {/* Merged table info + edit panel */}
                                                <section className="side-section" ref={editTableRef}>
                                                    <div className="vm-table-title-row">
                                                        <h4 className="vm-table-title">Table {selectedRoom.name}</h4>
                                                        {selectedRoom.isHeadTable && (
                                                            <StatusBadge variant="warning">Head Table</StatusBadge>
                                                        )}
                                                    </div>
                                                    {selectedRoom.groupName && (
                                                        <div className="vm-table-group-row">
                                                            {selectedRoom.groupColor && (
                                                                <span className="vm-table-group-dot" style={{ /* ds-dynamic: group color from data */ background: selectedRoom.groupColor }} />
                                                            )}
                                                            <span className="vm-table-group-name">{selectedRoom.groupName}</span>
                                                        </div>
                                                    )}
                                                    {selectedRoom.namedSeats && selectedRoom.namedSeats.length > 0 ? (
                                                        <div className="vm-table-seats-list">
                                                            {selectedRoom.namedSeats.map(s => (
                                                                <div key={s.seatIndex} className="vm-table-seat-row">
                                                                    <span className="vm-table-seat-index">#{s.seatIndex + 1}</span>
                                                                    <span>{s.guestName || <em>Empty</em>}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <p className="vm-table-no-guests">No guests assigned yet</p>
                                                    )}
                                                    <div className="vm-table-edit-divider">
                                                    <div className="form-row">
                                                        <div className="vm-field">
                                                            <label className="vm-label">Number</label>
                                                            <input
                                                                className="form-input form-input--sm"
                                                                value={tableEdit.num}
                                                                onChange={e => setTableEdit(t => t ? { ...t, num: e.target.value } : t)}
                                                                placeholder="1"
                                                                title="Sets the table's identity (shown as T1, T2… on Event Day) — must be a plain number for check-in and orders to match it correctly."
                                                            />
                                                        </div>
                                                        <div className="vm-field">
                                                            <label className="vm-label">Name <span className="form-label-hint">(optional)</span></label>
                                                            <input
                                                                className="form-input form-input--sm"
                                                                value={tableEdit.label}
                                                                onChange={e => setTableEdit(t => t ? { ...t, label: e.target.value } : t)}
                                                                placeholder="e.g. Head Table, VVIP 1"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="form-row">
                                                        <div className="vm-field">
                                                            <label className="vm-label">Zone <span className="form-label-hint">(optional)</span></label>
                                                            <SearchableSelect
                                                                className="form-input form-input--sm"
                                                                value={tableEdit.zone}
                                                                onChange={value => setTableEdit(t => t ? { ...t, zone: value } : t)}
                                                                placeholder="— unassigned —"
                                                                options={[
                                                                    { value: '', label: '— unassigned —' },
                                                                    ...activeFp.rooms.filter(r => r.type !== 'table').map(r => ({ value: r.name, label: r.name })),
                                                                ]}
                                                            />
                                                        </div>
                                                        <div className="vm-field">
                                                            <label className="vm-label">Seats</label>
                                                            <input
                                                                className="form-input form-input--sm"
                                                                type="number"
                                                                min="1"
                                                                max="30"
                                                                value={tableEdit.cap}
                                                                onChange={e => setTableEdit(t => t ? { ...t, cap: e.target.value } : t)}
                                                                placeholder="8"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Family/Group select */}
                                                    <div className="vm-field">
                                                        <label className="vm-label">Family / Group</label>
                                                        <SearchableSelect
                                                            className="form-input form-input--sm"
                                                            value={tableEdit.groupId}
                                                            onChange={value => setTableEdit(t => t ? { ...t, groupId: value } : t)}
                                                            placeholder="— unassigned —"
                                                            options={[
                                                                { value: '', label: '— unassigned —' },
                                                                ...guestGroups.map(g => ({ value: g.id, label: g.name })),
                                                            ]}
                                                        />
                                                    </div>

                                                    {/* Head Table checkbox */}
                                                    <div className="vm-field vm-field--inline">
                                                        <label className="vm-label vm-label--checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={tableEdit.isHeadTable}
                                                                onChange={e => setTableEdit(t => t ? { ...t, isHeadTable: e.target.checked } : t)}
                                                            />
                                                            Head Table (VVIP)
                                                        </label>
                                                    </div>

                                                    {/* Named seat slots (only when head table + seats defined) */}
                                                    {tableEdit.isHeadTable && namedSeatsCount > 0 && (
                                                        <div className="vm-named-seats">
                                                            <span className="form-section-title">Seat Assignments</span>
                                                            {Array.from({ length: namedSeatsCount }, (_, i) => {
                                                                const existing = selectedRoom.namedSeats?.find(s => s.seatIndex === i);
                                                                return (
                                                                    <div key={i} className="vm-named-seat-row">
                                                                        <span className="vm-seat-num">#{i + 1}</span>
                                                                        <SearchableSelect
                                                                            className="form-input form-input--sm vm-seat-select"
                                                                            value={existing?.guestId ?? ''}
                                                                            onChange={gId => {
                                                                                const gName = guests.find(g => g.id === gId)?.name ?? '';
                                                                                const prevSeats = selectedRoom.namedSeats ?? [];
                                                                                const updated = [
                                                                                    ...prevSeats.filter(s => s.seatIndex !== i),
                                                                                    ...(gId ? [{ seatIndex: i, guestId: gId, guestName: gName }] : []),
                                                                                ];
                                                                                handleRoomUpdate(selectedRoom.id, { namedSeats: updated });
                                                                            }}
                                                                            options={guests.map(g => ({ value: g.id, label: g.name }))}
                                                                            placeholder="— empty —"
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    </div>
                                                    <div className="vm-table-actions-row">
                                                        <button
                                                            className="primary-btn vm-table-apply-btn"
                                                            onClick={() => {
                                                                if (!selectedRoomId || !tableEdit) return;
                                                                const selectedGroup = guestGroups.find(g => g.id === tableEdit.groupId);
                                                                const capNum = tableEdit.cap ? Number(tableEdit.cap) : undefined;
                                                                handleRoomUpdate(selectedRoomId, {
                                                                    name: tableEdit.num.trim() || selectedRoom.name,
                                                                    assignedPurpose: tableEdit.label.trim() || undefined,
                                                                    capacity: capNum,
                                                                    seats: capNum,
                                                                    section: tableEdit.zone || undefined,
                                                                    groupId: tableEdit.groupId || undefined,
                                                                    groupName: selectedGroup?.name,
                                                                    groupColor: selectedGroup?.color,
                                                                    isHeadTable: tableEdit.isHeadTable,
                                                                });
                                                            }}
                                                        >
                                                            Apply
                                                        </button>
                                                        <button
                                                            className="vm-table-remove-btn"
                                                            onClick={() => {
                                                                if (selectedRoomId) handleRoomRemove(selectedRoomId);
                                                                setSelectedRoomId(null);
                                                                setTableEdit(null);
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </section>
                                            </>
                                        ) : selectedRoom ? (
                                            <section className="side-section">
                                                <h4>{selectedRoom.name}</h4>
                                                {/* Inline purpose input for non-table zones */}
                                                <div className="vm-field">
                                                    <label className="vm-label">Purpose <span className="form-label-hint">(optional)</span></label>
                                                    <input
                                                        key={`purpose-${selectedRoom.id}`}
                                                        className="form-input form-input--sm"
                                                        defaultValue={selectedRoom.assignedPurpose ?? ''}
                                                        placeholder="e.g. Bride's family seating"
                                                        onBlur={e => handleRoomUpdate(selectedRoom.id, { assignedPurpose: e.target.value.trim() || undefined })}
                                                    />
                                                </div>

                                                <div className="vm-field">
                                                    <label className="vm-label">Capacity <span className="form-label-hint">(max headcount for this zone)</span></label>
                                                    <input
                                                        key={`capacity-${selectedRoom.id}`}
                                                        ref={capacityInputRef}
                                                        className="form-input form-input--sm"
                                                        type="number"
                                                        min="0"
                                                        defaultValue={selectedRoom.capacity ?? ''}
                                                        placeholder="e.g. 220"
                                                        onBlur={e => {
                                                            const val = parseInt(e.target.value);
                                                            handleRoomUpdate(selectedRoom.id, { capacity: isNaN(val) ? undefined : val });
                                                        }}
                                                    />
                                                    {(() => {
                                                        const estimate = estimateZoneCapacity(selectedRoom, activeFp);
                                                        if (estimate == null || estimate === selectedRoom.capacity) return null;
                                                        return (
                                                            <div className="vm-capacity-suggestion">
                                                                <span>≈ {estimate} guests based on drawn area</span>
                                                                <button
                                                                    type="button"
                                                                    className="vm-capacity-suggestion-btn"
                                                                    onClick={() => {
                                                                        if (capacityInputRef.current) capacityInputRef.current.value = String(estimate);
                                                                        handleRoomUpdate(selectedRoom.id, { capacity: estimate });
                                                                    }}
                                                                >Use</button>
                                                            </div>
                                                        );
                                                    })()}
                                                    {!!selectedRoom.capacity && !!selectedVenue.capacity && selectedRoom.capacity > selectedVenue.capacity && (
                                                        <p className="vm-capacity-warning">
                                                            This zone's capacity ({selectedRoom.capacity}) is larger than the venue's overall capacity ({selectedVenue.capacity}).
                                                        </p>
                                                    )}
                                                </div>

                                                {/* GAP 6: Zone capacity summary card */}
                                                {zoneStats && (
                                                    <div className="vm-zone-capacity-card">
                                                        <div className="stat-row">
                                                            <span>Tables in zone</span>
                                                            <strong>{zoneStats.tableCount}</strong>
                                                        </div>
                                                        <div className="stat-row">
                                                            <span>Total seats</span>
                                                            <strong>{zoneStats.totalSeats}</strong>
                                                        </div>
                                                        {selectedRoom.capacity && (
                                                            <div className="stat-row">
                                                                <span>Zone capacity</span>
                                                                <strong style={{ /* ds-dynamic: capacity threshold color computed from data */
                                                                    color: zoneStats.totalSeats > selectedRoom.capacity
                                                                        ? '#dc2626'
                                                                        : zoneStats.totalSeats >= selectedRoom.capacity * 0.9
                                                                            ? '#16a34a'
                                                                            : '#f59e0b'
                                                                }}>
                                                                    {zoneStats.totalSeats}/{selectedRoom.capacity}
                                                                </strong>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {zoneTables.length > 0 ? (
                                                    <div className="vm-zone-tables">
                                                        {zoneTables.map(t => (
                                                            <div key={t.id} className="vm-zone-table-row">
                                                                <span className="vm-zone-table-num">T{t.number}</span>
                                                                <span className="vm-zone-table-name">{t.name}</span>
                                                                <span className="vm-zone-table-cap">{t.capacity}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="vm-zones-empty">
                                                        {seatingTables.length === 0
                                                            ? 'Seating tables not yet loaded.'
                                                            : 'No tables linked to this zone.'}
                                                    </p>
                                                )}
                                            </section>
                                        ) : null}
                                    </>
                                );
                            })()}

                            {/* GAP 7: Groups summary panel */}
                            {activeFp && (() => {
                                const groupsWithTables = getGroupsWithTables(activeFp);
                                if (groupsWithTables.length === 0) return null;
                                return (
                                    <section className="side-section">
                                        <h4>Groups</h4>
                                        <div className="vm-group-list">
                                            {groupsWithTables.map(({ group, tableCount, totalSeats, guestCount }) => (
                                                <button
                                                    key={group.id}
                                                    className="vm-group-row"
                                                    onClick={() => window.dispatchEvent(new CustomEvent('floor-plan-highlight-group', { detail: group.id }))}
                                                    title="Click to highlight on canvas"
                                                >
                                                    <span className="vm-group-dot" style={{ /* ds-dynamic: group color from data */ background: group.color ?? '#9ca3af' }} />
                                                    <span className="vm-group-name">{group.name}</span>
                                                    <span className="vm-group-stats">{tableCount} tbl · {guestCount}/{totalSeats}</span>
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            className="vm-copy-summary-btn"
                                            onClick={() => {
                                                const zones = activeFp.rooms.filter(r => r.type !== 'table');
                                                const tables = activeFp.rooms.filter(r => r.type === 'table');
                                                const lines: string[] = [`Floor Plan: ${activeFp.name}`, ''];
                                                zones.forEach(zone => {
                                                    const zoneTables = tables.filter(t => t.section === zone.name || t.groupName === zone.name);
                                                    const filled = zoneTables.filter(t => statusMap[t.id] === 'full').length;
                                                    const partial = zoneTables.filter(t => statusMap[t.id] === 'partial').length;
                                                    lines.push(`${zone.name} (${zone.type}): ${zoneTables.length} tables, ${filled} full, ${partial} partial`);
                                                });
                                                if (groupsWithTables.length > 0) {
                                                    lines.push('', 'Groups:');
                                                    groupsWithTables.forEach(({ group, tableCount, totalSeats, guestCount }) => {
                                                        lines.push(`  ${group.name}: ${guestCount}/${totalSeats} seats · ${tableCount} tables`);
                                                    });
                                                }
                                                navigator.clipboard.writeText(lines.join('\n'));
                                                setSummaryCopied(true);
                                                setTimeout(() => setSummaryCopied(false), 2000);
                                            }}
                                        >
                                            {summaryCopied ? 'Copied!' : 'Copy Summary'}
                                        </button>
                                    </section>
                                );
                            })()}

                            {saved && <p className="vm-saved-msg">Saved</p>}
                        </div>
                    )}
                </div>

        </div>

        {/* Template picker modal */}
        {showTemplateModal && (
            <div className="vm-template-backdrop" onClick={() => setShowTemplateModal(false)}>
                <div className="vm-template-modal" onClick={e => e.stopPropagation()}>
                    <h3 className="vm-template-title">Choose a floor plan template</h3>
                    <div className="vm-template-options">
                        <button className="vm-template-card" onClick={() => createFloorPlanFromTemplate('blank')}>
                            <span className="vm-template-icon"><Icon name="plus" size={22} /></span>
                            <strong>Blank</strong>
                            <span className="vm-template-desc">Start with an empty canvas</span>
                        </button>
                        {savedTemplates.map(t => (
                            <div key={t.id} className="vm-template-card vm-template-card--saved">
                                <button
                                    className="vm-template-card-btn"
                                    onClick={() => createFloorPlanFromTemplate(t.id)}
                                >
                                    <span className="vm-template-icon"><Icon name="map" size={22} /></span>
                                    <strong>{t.name}</strong>
                                    <span className="vm-template-desc">{t.description || `${t.rooms.length} rooms`}</span>
                                </button>
                                <button
                                    className="vm-template-delete-btn"
                                    onClick={(e) => { e.stopPropagation(); setConfirmDeleteTemplate({ id: t.id, name: t.name }); }}
                                    title="Delete template"
                                >
                                    <Icon name="trash" size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                    {savedTemplates.length === 0 && (
                        <p className="form-hint">
                            No saved templates yet — open a floor plan you like and use "Save as Template" to reuse its layout here.
                        </p>
                    )}
                    <button className="vm-template-cancel" onClick={() => setShowTemplateModal(false)}>Cancel</button>
                </div>
            </div>
        )}

        {/* Save current floor plan as a reusable template */}
        {showSaveTemplateModal && (
            <div className="vm-template-backdrop" onClick={() => setShowSaveTemplateModal(false)}>
                <div className="vm-template-modal vm-save-template-modal" onClick={e => e.stopPropagation()}>
                    <h3 className="vm-template-title">Save as template</h3>
                    <label className="form-label">Name<span className="form-required">*</span></label>
                    <input
                        className="form-input"
                        value={saveTemplateName}
                        onChange={e => setSaveTemplateName(e.target.value)}
                        placeholder="e.g. Traditional Wedding"
                        autoFocus
                        required
                    />
                    <label className="form-label">Description (optional)</label>
                    <input
                        className="form-input"
                        value={saveTemplateDesc}
                        onChange={e => setSaveTemplateDesc(e.target.value)}
                        placeholder="e.g. 800 pax — Family sections, VIP, food stations"
                    />
                    <div className="vm-save-template-actions">
                        <button className="vm-template-cancel" onClick={() => setShowSaveTemplateModal(false)}>Cancel</button>
                        <button
                            className="header-action-btn primary"
                            disabled={!saveTemplateName.trim()}
                            onClick={handleConfirmSaveTemplate}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        )}

        {sharedFloorPlan && (
            <SharedFloorPlanView
                floorPlan={sharedFloorPlan}
                venueName={venues.find(v => v.id === sharedFloorPlan.venueId)?.name ?? selectedVenue?.name ?? 'Shared floor plan'}
                guestGroups={guestGroups}
                guests={guests}
                onClose={closeSharedFloorPlan}
            />
        )}

        <ConfirmDialog
            {...foreverVenue.dialogProps}
            title="Delete this venue forever?"
            subject={foreverVenue.target?.name}
            description="This cannot be undone."
            consequences={['The venue and its contact details', 'Its floor plans and room layouts']}
            confirmLabel="Delete forever"
        />
        <ConfirmDialog
            open={confirmDeleteTemplate !== null}
            onClose={() => setConfirmDeleteTemplate(null)}
            onConfirm={() => confirmDeleteTemplate && handleDeleteTemplate(confirmDeleteTemplate.id)}
            title="Delete template?"
            description={confirmDeleteTemplate ? `Delete template "${confirmDeleteTemplate.name}"?` : undefined}
        />

        </>
    );
};

export default VenueManager;
